import { useEffect, useRef } from 'react';

export const PICKUP_ANALYSIS_LIVE_REFRESH_MS = 60_000;

interface UsePickupAnalysisLiveRefreshOptions {
  refreshReads: () => Promise<void>;
  paused: boolean;
  intervalMs?: number;
}

export function usePickupAnalysisLiveRefresh({
  refreshReads,
  paused,
  intervalMs = PICKUP_ANALYSIS_LIVE_REFRESH_MS
}: UsePickupAnalysisLiveRefreshOptions): void {
  const refreshReadsRef = useRef(refreshReads);
  const pausedRef = useRef(paused);
  const inFlightRef = useRef(false);

  useEffect(() => {
    refreshReadsRef.current = refreshReads;
  }, [refreshReads]);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    let active = true;

    const refresh = async () => {
      if (!active || pausedRef.current || inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      try {
        await refreshReadsRef.current();
      } catch {
        // Each read owns its user-facing/logging behavior; a failed poll must
        // not become an unhandled browser rejection or stop later refreshes.
      } finally {
        inFlightRef.current = false;
      }
    };

    const handleFocus = () => {
      void refresh();
    };
    const timer = window.setInterval(() => void refresh(), intervalMs);
    window.addEventListener('focus', handleFocus);

    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', handleFocus);
    };
  }, [intervalMs]);
}
